/**
 * Maps Appeal Info form values to backend `pharmacy/appeal/update` payload fields.
 */

import { CaseAppealsTableRowData } from "../../../../data-model/caseAppealsTable";
import { UpdateAppealPayload } from "../../../../api/postCall/updateAppeal";
import { AppealType, AppealTypeLabels } from "../../../../enums/appealType";
import { resolveAppealStatusValue } from "../../appealSummaryModal/utils/appealStatusOptions";
import { AppealInfoFormData } from "./buildAppealInfoInitialData";

const FRONTEND_TO_BACKEND_APPEAL_TYPE: Record<string, string> = {
  [AppealType.FAX]: "Fax",
  [AppealType.PHONE]: "Call",
  [AppealType.PORTAL]: "E-Appeal",
  [AppealType.MAIL]: "Manual",
};

const BACKEND_APPEAL_TYPE_VALUES = new Set([
  "Fax",
  "Call",
  "E-Appeal",
  "Manual",
]);

/**
 * Resolves a form or API appeal type value to the backend enum string.
 *
 * @param value - Appeal type from form state or table row
 * @returns Backend appeal type (e.g. `Fax`)
 */
export const resolveAppealTypeForApi = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Fax";
  }

  if (BACKEND_APPEAL_TYPE_VALUES.has(trimmed)) {
    return trimmed;
  }

  const normalized = trimmed.toLowerCase().replace(/\s+/g, "_");
  if (FRONTEND_TO_BACKEND_APPEAL_TYPE[normalized]) {
    return FRONTEND_TO_BACKEND_APPEAL_TYPE[normalized];
  }

  const enumMatch = Object.values(AppealType).find((type) => type === normalized);
  if (enumMatch) {
    return FRONTEND_TO_BACKEND_APPEAL_TYPE[enumMatch] ?? AppealTypeLabels[enumMatch];
  }

  return trimmed;
};

/**
 * Resolves a form or API appeal status value to the backend enum string.
 * Form values are already backend strings; this also accepts legacy snake_case ids.
 *
 * @param value - Appeal status from form state or table row
 * @returns Backend appeal status (e.g. `Not Started`)
 */
export const resolveAppealStatusForApi = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Not Started";
  }

  return resolveAppealStatusValue(trimmed) || trimmed;
};

/**
 * Builds the POST body for `pharmacy/appeal/update` from side panel form data.
 *
 * Appeal Info does not edit POC, so `appeal_poc` is only forwarded when the
 * source row already has one. Omitting it avoids posting `null` and wiping an
 * assignment set from Case Appeals or Appeal Summary.
 *
 * @param data - Selected Case Appeals table row
 * @param appealInfo - Appeal Info side panel form values
 * @returns Update appeal request payload
 */
export const buildUpdateAppealPayload = (
  data: CaseAppealsTableRowData,
  appealInfo: AppealInfoFormData,
): UpdateAppealPayload => {
  const appealReason = appealInfo.appealReason.trim();
  const appealComment = appealInfo.appealComments.trim();
  const appealPoc = String(data.appeal_poc ?? "").trim();

  return {
    identifier: data.identifier,
    appeal_status: resolveAppealStatusForApi(appealInfo.appealStatus),
    appeal_type: resolveAppealTypeForApi(appealInfo.appealType),
    ...(appealPoc ? { appeal_poc: appealPoc } : {}),
    appeal_comment: appealComment || null,
    appeal_reason: appealReason === "N/A" ? null : appealReason || null,
  };
};
